import React, {useState, useEffect} from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import {get, post, put} from '../utils/fetchAPI'
import {Toast} from '../utils/SwalUti'

const RELATIONSHIPS = ['father', 'mother', 'spouse', 'son', 'daughter', 'brother', 'sister', 'other']

const emptyForm = {
    first: '',
    last: '',
    relationship: 'other',
    dateOfBirth: null,
    sex: 'male',
    mobile: '',
    home: '',
    addressLineOne: '',
    addressLineTwo: '',
    city: '',
    state: '',
    zip: '',
    insurance: 'Enabled',
}

// Shared modal for adding or editing a Patient.
//
// Props:
//   show          — controls visibility
//   onHide        — called on close
//   onSaved       — called after a successful save (parent should refresh its list)
//   initialPatient — null for add, the Patient document for edit
//   accountId     — target account on create (staff-cross-account flow). Optional;
//                   when omitted, backend defaults to the calling user's account.
function PatientFormModal({show, onHide, onSaved, initialPatient, accountId}) {
    const [formData, setFormData] = useState(emptyForm)
    const [stateList, setStateList] = useState([])
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')

    const isEdit = !!initialPatient
    const isSelf = !!(initialPatient && initialPatient.isSelf)

    useEffect(() => {
        if (show) {
            if (initialPatient) {
                const dob = initialPatient.dateOfBirth ? new Date(initialPatient.dateOfBirth) : null
                setFormData({
                    ...emptyForm,
                    ...initialPatient,
                    dateOfBirth: dob,
                })
            } else {
                setFormData(emptyForm)
            }
            setError('')
            if (stateList.length === 0) {
                get('/states').then(res => setStateList(res.data.states)).catch(() => {})
            }
        }
    }, [show, initialPatient])

    const handleChange = (e) => {
        setFormData({...formData, [e.target.name]: e.target.value})
    }

    const handleDob = (date) => {
        setFormData({...formData, dateOfBirth: date})
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError('')
        setSubmitting(true)
        try {
            const payload = {...formData}
            // Strip server-managed fields if editing.
            delete payload._id
            delete payload.account
            delete payload.hcp
            delete payload.isSelf
            delete payload.legacyUserId
            delete payload.createdAt
            delete payload.updatedAt
            delete payload.__v
            if (isSelf) {
                // The self patient's relationship is fixed; backend rejects changes.
                payload.relationship = 'self'
            }

            if (isEdit) {
                await put(`/patients/${initialPatient._id}`, payload)
                Toast.fire({icon: 'success', title: 'Patient updated'})
            } else {
                if (accountId) payload.accountId = accountId
                await post('/patients', payload)
                Toast.fire({icon: 'success', title: 'Patient added'})
            }
            onSaved && onSaved()
            onHide && onHide()
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Save failed')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false}>
            <Modal.Header closeButton className='pb-2'>
                <Modal.Title>{isEdit ? 'Edit Patient' : 'Add Patient'}</Modal.Title>
            </Modal.Header>
            <form onSubmit={handleSubmit}>
                <Modal.Body>
                    <div className="form-row">
                        <div className="form-group col-md-6 px-2">
                            <label>First Name</label>
                            <input type="text" name="first" className="form-control"
                                   value={formData.first || ''} onChange={handleChange} required/>
                        </div>
                        <div className="form-group col-md-6 px-2">
                            <label>Last Name</label>
                            <input type="text" name="last" className="form-control"
                                   value={formData.last || ''} onChange={handleChange} required/>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group col-md-6 px-2">
                            <label>Relationship</label>
                            <select name="relationship" className="form-control"
                                    value={isSelf ? 'self' : formData.relationship}
                                    onChange={handleChange} disabled={isSelf}>
                                {isSelf && <option value="self">Self</option>}
                                {!isSelf && RELATIONSHIPS.map(r => (
                                    <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group col-md-6 px-2">
                            <label>Sex</label>
                            <select name="sex" className="form-control"
                                    value={formData.sex || 'male'} onChange={handleChange}>
                                <option value={"male"}>Male</option>
                                <option value={"female"}>Female</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group col-md-6 px-2">
                            <label>Date of Birth</label>
                            <DatePicker
                                className="form-control"
                                dateFormat="MM/dd/yyyy"
                                selected={formData.dateOfBirth}
                                onChange={handleDob}
                                placeholderText="MM/dd/yyyy"
                                showYearDropdown
                                yearDropdownItemNumber={20}
                                dropdownMode="select"
                            />
                        </div>
                        <div className="form-group col-md-6 px-2">
                            <label>Insurance</label>
                            <select name="insurance" className="form-control"
                                    value={formData.insurance || 'Enabled'} onChange={handleChange}>
                                <option value="Enabled">Enabled</option>
                                <option value="Disabled">Disabled</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group col-md-6 px-2">
                            <label>Cell Phone</label>
                            <input type="text" name="mobile" className="form-control"
                                   value={formData.mobile || ''} onChange={handleChange}/>
                        </div>
                        <div className="form-group col-md-6 px-2">
                            <label>Home Phone</label>
                            <input type="text" name="home" className="form-control"
                                   value={formData.home || ''} onChange={handleChange}/>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group col-md-6 px-2">
                            <label>Address line 1</label>
                            <input type="text" name="addressLineOne" className="form-control"
                                   value={formData.addressLineOne || ''} onChange={handleChange}/>
                        </div>
                        <div className="form-group col-md-6 px-2">
                            <label>Address line 2</label>
                            <input type="text" name="addressLineTwo" className="form-control"
                                   value={formData.addressLineTwo || ''} onChange={handleChange}/>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group col-md-4 px-2">
                            <label>City</label>
                            <input type="text" name="city" className="form-control"
                                   value={formData.city || ''} onChange={handleChange}/>
                        </div>
                        <div className="form-group col-md-4 px-2">
                            <label>State</label>
                            <select name="state" className="form-control"
                                    value={formData.state || ''} onChange={handleChange}>
                                <option value="">Select...</option>
                                {stateList.map((s, i) => <option key={i} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="form-group col-md-4 px-2">
                            <label>Zip Code</label>
                            <input type="text" name="zip" className="form-control"
                                   value={formData.zip || ''} onChange={handleChange}/>
                        </div>
                    </div>

                    {error && <div className="text-danger px-2">{error}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={submitting}>Close</Button>
                    <Button variant="primary" type="submit" disabled={submitting}>
                        {submitting ? 'Saving…' : 'Save'}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    )
}

export default PatientFormModal
